/**
 * MapView Constants and Types
 *
 * Non-component exports extracted from MapView.tsx to satisfy
 * react-refresh/only-export-components lint rule.
 *
 * Requirements: 3.1, 3.2, 3.3, 3.4, 3.5, 4.1, 4.3, 5.3, 5.5, 11.2, 15.1, 15.2
 */

import type { ExpressionSpecification } from '@maplibre/maplibre-gl-style-spec';
import type { AttributionControlOptions } from 'maplibre-gl';
import type { AreaColorDimension } from '../../../stores/mapStore';

/**
 * A MapLibre GL expression for data-driven styling.
 *
 * This is the canonical spec type, re-exported so the rest of the map code has
 * a single name for it. `mapbox-gl`'s types accepted a loose
 * `[string, ...unknown[]]`; MapLibre's are precise, which means expressions
 * assembled at runtime (colour `match` chains, opacity `interpolate` ramps)
 * can't be inferred structurally and need a cast where they're built. Those
 * casts live in the builder functions in `MapView.utils.ts`, so every consumer
 * gets a properly typed expression.
 */
export type MapExpression = ExpressionSpecification;

/**
 * Debounce delay for year changes in milliseconds.
 * Requirement 11.2: THE MapView SHALL debounce year change events to prevent excessive API calls
 */
export const YEAR_CHANGE_DEBOUNCE_MS = 300;

/**
 * Hover information for province interactions
 * Requirement 7.4: WHEN the user hovers over a province, THE MapView SHALL display hover info
 */
export interface HoverInfo {
  /** Longitude and latitude of hover position */
  lngLat: [number, number];
  /** Feature properties from the hovered layer */
  feature: Record<string, unknown>;
  /** Province ID if available */
  provinceId: string | undefined;
}

/**
 * Sidebar width constants for layout calculations
 * SIDEBAR_WIDTH_OPEN = 50px sidebar + 300px MenuDrawer = 350px total
 * SIDEBAR_WIDTH_CLOSED = 50px sidebar only
 */
export const SIDEBAR_WIDTH_OPEN = 350;
export const SIDEBAR_WIDTH_CLOSED = 50;

/**
 * Right drawer width for layout calculations -- must match RightDrawer CSS width (35%)
 */
export const RIGHT_DRAWER_WIDTH_PERCENT = 35;

/**
 * Province layer configuration for each color dimension.
 * Requirement 3.1, 3.2, 3.3, 3.4: Province coloring by dimension
 */
export interface ProvinceLayerConfig {
  /** Layer ID */
  id: string;
  /** Property name in GeoJSON feature (r, c, e, g, p) */
  property: string;
  /** Color dimension this layer represents */
  dimension: AreaColorDimension;
}

/**
 * Province layer configurations for all dimensions.
 * Requirements: 3.1, 3.2, 3.3, 3.4, 4.1, 4.3
 */
export const PROVINCE_LAYERS: ProvinceLayerConfig[] = [
  { id: 'ruler-fill', property: 'r', dimension: 'ruler' },
  { id: 'culture-fill', property: 'c', dimension: 'culture' },
  { id: 'religion-fill', property: 'e', dimension: 'religion' },
  { id: 'religionGeneral-fill', property: 'g', dimension: 'religionGeneral' },
  { id: 'population-fill', property: 'p', dimension: 'population' },
];

/**
 * Default population fill color for population dimension.
 * Requirement 3.5: Population opacity interpolation
 */
export const POPULATION_FILL_COLOR = '#4a90d9';

/**
 * Population opacity range for interpolation.
 * Requirement 3.4: Opacity range [0.3, 1.0]
 */
export const POPULATION_OPACITY_MIN = 0.3;
export const POPULATION_OPACITY_MAX = 1.0;

/**
 * Maximum population value for opacity interpolation.
 * Requirement 3.4: THE MapView SHALL normalize population values to an opacity range of 0.3 to 1.0
 */
export const MAX_POPULATION_FOR_OPACITY = 10000000; // 10 million

/**
 * Default fill opacity for categorical layers.
 */
export const DEFAULT_FILL_OPACITY = 0.6;

/*
 * Requirement 5.5 (per-type marker icons) is implemented in `MapView.tsx`, not
 * here: `MARKER_SPRITE_ICONS` slices Chronas's own sprite sheet into `marker-b`,
 * `marker-c`, `marker-cp`, `marker-p`, … and registers them with
 * `map.setMissingStyleImageResolver`. The `MARKER_ICONS` / `DEFAULT_MARKER_ICON`
 * / `MARKER_ICON_SIZE` constants that used to live here named Mapbox **Maki**
 * sprite ids (`castle`, `town-hall`, `star`, `monument`, `information`). They
 * had no reader anywhere in the app, and Maki does not exist on OpenFreeMap's
 * sprite — so they were only ever a false lead. Removed with the Mapbox
 * migration rather than translated.
 */

/**
 * Marker icon colors by type.
 * Requirement 5.3: THE MapView SHALL support marker types with distinct styling
 */
export const MARKER_COLORS: Record<string, string> = {
  battle: '#e74c3c', // Red for battles
  city: '#3498db', // Blue for cities
  capital: '#f1c40f', // Gold for capitals
  person: '#9b59b6', // Purple for persons
  event: '#2ecc71', // Green for events
  other: '#95a5a6', // Gray for other
};

/**
 * Attribution control options for the basemap.
 *
 * MapLibre derives most of this automatically: the OpenFreeMap styles resolve
 * their vector source through `https://tiles.openfreemap.org/planet`, whose
 * TileJSON carries the OpenFreeMap / OpenMapTiles / OpenStreetMap credits, and
 * `public/styles/satellite-eox.json` declares its EOX credit on the source.
 *
 * OpenStreetMap data is ODbL-licensed, so displaying attribution is a licence
 * obligation, not a nicety — the control must stay enabled. `ne2_shaded`
 * (the shaded-relief raster in the `liberty` style) declares no attribution of
 * its own, so Natural Earth is credited here.
 */
/**
 * Ids of the GeoJSON sources Chronas adds on top of the basemap.
 *
 * The label effects in `MapView` walk `map.getStyle().layers`, which contains
 * our declarative `<Layer>`s alongside the basemap's. Without this set the
 * locale rewrite would also overwrite the `text-field` of `markers-label` and
 * `area-labels-points` — harmless in output (their coalesce chain still falls
 * through to `name`) but it fights react-map-gl, which owns those layout
 * properties declaratively and re-applies them on re-render.
 */
export const OWN_SOURCE_IDS: ReadonlySet<string> = new Set([
  'provinces',
  'area-hover',
  'markers',
  'area-label-lines',
  'area-labels',
  'entity-outline',
]);

export const ATTRIBUTION_OPTIONS: AttributionControlOptions = {
  compact: true,
  customAttribution: [
    'Relief: <a href="https://www.naturalearthdata.com/" target="_blank" rel="noreferrer">Natural Earth</a>',
  ],
};
